import type { FieldErrors, FieldError } from 'react-hook-form';

/**
 * React Hook Form의 FieldErrors에서 nested 필드의 에러를 안전하게 가져오는 타입 헬퍼
 *
 * @example
 * // Before (as any 사용)
 * const error = (errors as any)?.getPasswords?.getPassword?.message;
 *
 * // After (타입 안전)
 * const error = getNestedError(errors, 'getPasswords', 'getPassword')?.message;
 */
export function getNestedError<T extends Record<string, any>>(
	errors: FieldErrors<T>,
	parentKey: string,
	childKey: string
): FieldError | undefined {
	// 방법 1: errors.getPasswords.getPassword (nested object 형식)
	const parentError = errors[parentKey as keyof T];
	if (parentError && typeof parentError === 'object' && !('message' in parentError)) {
		const nestedErrors = parentError as Record<string, FieldError>;
		return nestedErrors[childKey];
	}

	// 방법 2: errors['getPasswords.getPassword'] (flat string key 형식)
	const flatKey = `${parentKey}.${childKey}`;
	const flatError = errors[flatKey as keyof T];
	if (flatError && typeof flatError === 'object' && 'message' in flatError) {
		return flatError as FieldError;
	}

	return undefined;
}

/**
 * nested 필드에 에러가 있는지 확인하는 헬퍼
 *
 * @example
 * const hasError = hasNestedError(errors, 'getPasswords', 'getPassword');
 */
export function hasNestedError<T extends Record<string, any>>(
	errors: FieldErrors<T>,
	parentKey: string,
	childKey: string
): boolean {
	return getNestedError(errors, parentKey, childKey) !== undefined;
}

/**
 * nested 필드의 에러 메시지를 안전하게 가져오는 헬퍼
 *
 * @example
 * const message = getNestedErrorMessage(errors, 'getPasswords', 'getPassword');
 */
export function getNestedErrorMessage<T extends Record<string, any>>(
	errors: FieldErrors<T>,
	parentKey: string,
	childKey: string
): string | undefined {
	const error = getNestedError(errors, parentKey, childKey);
	if (error && typeof error.message === 'string') {
		return error.message;
	}
	return undefined;
}

/**
 * 여러 nested 필드 중 하나라도 에러가 있는지 확인
 *
 * @example
 * const hasAnyError = hasAnyNestedError(
 *   errors,
 *   ['getPasswords', 'getPassword'],
 *   ['getPasswords', 'confirmPassword']
 * );
 */
export function hasAnyNestedError<T extends Record<string, any>>(
	errors: FieldErrors<T>,
	...paths: [string, string][]
): boolean {
	return paths.some(([parentKey, childKey]) => hasNestedError(errors, parentKey, childKey));
}
